import React, {ChangeEvent, FC, ReactNode, useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import TimeRangeWarningModal from './TimeRangeWarningModal';
import {DateMoment, TimeScale} from '../../../../../../types';
import configActions from '../../../../../../actions/config.actions';

export interface TimeRangeLimitWarningProviderProps {
    children: ReactNode;
}

export interface LimitWarningState {
    startDate?: DateMoment;
    endDate?: DateMoment;
    timeScale?: TimeScale;
    confirmCallback: () => void;
    cancelCallback?: () => void;
}

export interface TimeRangeLimitWarningContextValue {
    onOpen: (state: LimitWarningState) => void;
    onConfirm: () => void;
    onCancel: () => void;
    state: LimitWarningState | null;
    open: boolean;
    doNotShowAgain: boolean;
    setDoNotShow: (state: boolean) => void;
}

export const TimeRangeLimitWarningContext = React.createContext<TimeRangeLimitWarningContextValue>({
    onOpen: () => {},
    onConfirm: () => {},
    onCancel: () => {},
    state: null,
    open: false,
    doNotShowAgain: false,
    setDoNotShow: () => {}
});

const TimeRangeLimitWarningProvider: FC<TimeRangeLimitWarningProviderProps> = ({
    children
}: TimeRangeLimitWarningProviderProps) => {
    const isDisableTimeRangeWarning = useSelector(state =>
        state.get('config').get('isDisableTimeRangeWarning')
    );
    const dispatch = useDispatch();

    const [state, setState] = useState<LimitWarningState | null>(null);
    const [doNotShowAgain, setDoNotShow] = useState(isDisableTimeRangeWarning);
    // eslint-disable-next-line
    const {cancelCallback, confirmCallback, ...timeRangeModalProps} = state || {};

    const storeSettings = () => {
        if (doNotShowAgain !== isDisableTimeRangeWarning) {
            dispatch(configActions.setTimeRangeWarningState(doNotShowAgain));
        }
    };

    const handleCancel = useCallback(() => {
        if (state && state.cancelCallback) {
            state.cancelCallback();
            storeSettings();
        }

        setState(null);
    }, [setState, doNotShowAgain, isDisableTimeRangeWarning]);

    const handleConfirm = useCallback(() => {
        if (state) {
            state.confirmCallback();
            setState(null);
            storeSettings();
        }
    }, [state, setState, doNotShowAgain, isDisableTimeRangeWarning]);

    const handleDoNotShowChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setDoNotShow(event.target.checked);
        },
        [setDoNotShow]
    );

    const value = useMemo(
        () => ({
            onOpen: setState,
            onConfirm: handleConfirm,
            onCancel: handleCancel,
            state,
            open: Boolean(state),
            doNotShowAgain,
            setDoNotShow
        }),
        [doNotShowAgain, handleConfirm, handleCancel, state]
    );

    return (
        <TimeRangeLimitWarningContext.Provider value={value}>
            {children}
            <TimeRangeWarningModal
                open={Boolean(state)}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                doNotShowAgain={doNotShowAgain}
                onDoNotShowChange={handleDoNotShowChange}
                {...timeRangeModalProps}
            />
        </TimeRangeLimitWarningContext.Provider>
    );
};

export default TimeRangeLimitWarningProvider;
